import { useRef, useState, useEffect } from 'react';
import { Flex, Icon, Tooltip, Box } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { useSharedCalendarPolling } from '@/components/shared/scheduling-preferences/useSharedCalendarPolling';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { KTag } from '@/ui-library/components/atoms/KTag';
import { DotFlashingLoader } from '@/ui-library/components/atoms/loader/DotFlashingLoader';
import { useMailBox } from '@/hooks/use-mailbox';
import { CALENDAR_PROVIDERS } from '@/ui-library/components/constants';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { selectUsers } from '@/store/users/selectors';
import { useSelector } from 'react-redux';

export const useCalendarSchedulingFields = ({
  updateSharedData,
  interviewDataRef,
  isSelfSchedule,
  isEdit,
  interviewPlan,
  isBulkManual = false,
  registerForUpdates,
}) => {
  const fetchCalendarNotNeeded =
    isEdit &&
    !isSelfSchedule &&
    interviewPlan?.scheduled_calendar?.calendar_name;

  const hasCustomChangeOccurred = useRef(false);
  const { isCalConnected, connectedCalValue } = useMailBox();
  const users = useSelector(selectUsers);

  // Track calendar_info changes to trigger re-renders for dynamic labelInfoText
  const [currentCalendarId, setCurrentCalendarId] = useState<string | null>(
    interviewDataRef?.current?.calendar_info?.provider_calendar_id || null
  );

  useEffect(() => {
    if (!registerForUpdates) return;

    const unregisterCalendarInfo = registerForUpdates(
      'calendar_info',
      (data) => {
        const { calendar_info } =
          data?.fullData ?? interviewDataRef?.current ?? {};
        const newCalendarId = calendar_info?.provider_calendar_id || null;
        setCurrentCalendarId((prev) =>
          prev === newCalendarId ? prev : newCalendarId
        );
      }
    );

    return () => {
      unregisterCalendarInfo();
    };
  }, [registerForUpdates, interviewDataRef]);

  // Check for cached calendar options in bulk mode
  const getCachedCalendarOptions = () => {
    if (isBulkManual) {
      try {
        const cached = localStorage.getItem('calendarOptionsCache_bulk');
        if (cached) {
          return JSON.parse(cached);
        }
      } catch (error) {
        console.error(
          '[CalendarField] Error loading cached calendar options:',
          error
        );
      }
    }
    return null;
  };

  const cachedOptions = getCachedCalendarOptions();

  // In edit mode, also fetch if there's a non-primary scheduled calendar
  // (we need the calendar options to find the calendar owner's user_id)
  const hasNonPrimaryScheduledCalendar =
    isEdit &&
    interviewPlan?.scheduled_calendar?.provider_calendar_id &&
    !interviewPlan?.scheduled_calendar?.is_primary;

  // In edit mode with Microsoft calendar, always fetch calendar options if there's a scheduled_calendar
  // This is needed to determine the calendar owner for showing warning icons
  const shouldFetchForMicrosoftEdit =
    isEdit &&
    connectedCalValue === CALENDAR_PROVIDERS.MICROSOFT &&
    interviewPlan?.scheduled_calendar?.provider_calendar_id;

  const shouldAutoFetch =
    !cachedOptions &&
    ((interviewPlan?.shared_calendar_enabled && isCalConnected) ||
      (hasNonPrimaryScheduledCalendar && isCalConnected) ||
      (shouldFetchForMicrosoftEdit && isCalConnected) ||
      false);

  const { isLoading, sharedCalendarOptions, isMaxRetriesExceeded } =
    useSharedCalendarPolling({
      type: 'saved',
      autoFetch: shouldAutoFetch,
    });

  // Cache the options when they're loaded in bulk mode
  if (isBulkManual && sharedCalendarOptions && !cachedOptions) {
    try {
      const dataToCache = JSON.stringify(sharedCalendarOptions);
      localStorage.setItem('calendarOptionsCache_bulk', dataToCache);
    } catch (error) {
      console.error('[CalendarField] Error caching calendar options:', error);
    }
  }

  // Use cached options if available, otherwise use fresh data
  const effectiveCalendarOptions = cachedOptions || sharedCalendarOptions;

  const isDefaultCalendar = effectiveCalendarOptions?.calendars?.find(
    (calendar) => calendar?.is_default
  );

  // Get the calendar owner user ID for a given calendar ID
  const getCalendarOwnerUserId = (calendarId?: string) => {
    if (connectedCalValue !== CALENDAR_PROVIDERS.MICROSOFT) return null;

    // In edit mode, use user_id directly from scheduled_calendar if available
    // This works even before calendar options are loaded
    if (isEdit && interviewPlan?.scheduled_calendar?.user_id) {
      return interviewPlan.scheduled_calendar.user_id;
    }

    // Check all possible sources for the calendar ID:
    // 1. Explicitly passed calendarId
    // 2. Current state (for dynamic updates)
    // 3. interviewDataRef (for real-time updates)
    // 4. Edit mode: scheduled_calendar
    // 5. Create mode: calendar_info from plan
    // 6. Default calendar
    const resolvedCalendarId =
      calendarId ||
      currentCalendarId ||
      interviewDataRef.current?.calendar_info?.provider_calendar_id ||
      interviewPlan?.scheduled_calendar?.provider_calendar_id ||
      interviewPlan?.calendar_info?.provider_calendar_id ||
      isDefaultCalendar?.provider_calendar_id;

    if (!resolvedCalendarId) return null;

    const selectedCalendar = effectiveCalendarOptions?.calendars?.find(
      (cal) => cal.provider_calendar_id === resolvedCalendarId
    );

    // If calendar not found in options (e.g., shared calendar from another user),
    // fall back to scheduled_by user as the calendar owner
    if (!selectedCalendar && isEdit && interviewPlan?.scheduled_by?.id) {
      return interviewPlan.scheduled_by.id;
    }

    return selectedCalendar?.user_id || null;
  };

  const calendarOwnerUserId = getCalendarOwnerUserId();

  // Store calendarOwnerUserId in shared data for interviewer field to access
  // When calendar options load async, calendarOwnerUserId changes from null to actual userId
  // triggering re-render and this update
  if (calendarOwnerUserId !== interviewDataRef.current?.calendarOwnerUserId) {
    updateSharedData('calendarOwnerUserId', calendarOwnerUserId);
  }

  const isPrimaryCalendarOnlyAvailable =
    effectiveCalendarOptions?.calendars?.length === 1 &&
    effectiveCalendarOptions?.calendars?.[0]?.is_primary;
  // Check if we should auto-update with default calendar
  // Only auto-update if no existing calendar info in plan and default is available
  // If there's calendar_info in the plan, it means user has made a selection previously
  // In edit mode, check scheduled_calendar as well
  const hasExistingCalendarSelection =
    !!interviewPlan?.calendar_info?.provider_calendar_id ||
    !!interviewPlan?.scheduled_calendar?.provider_calendar_id;

  // In edit mode, initialize calendar_info from scheduled_calendar if not already set
  if (
    isEdit &&
    interviewPlan?.scheduled_calendar?.provider_calendar_id &&
    !interviewDataRef.current?.calendar_info?.provider_calendar_id
  ) {
    updateSharedData('calendar_info', {
      provider_calendar_id:
        interviewPlan?.scheduled_calendar?.provider_calendar_id,
      is_primary: interviewPlan?.scheduled_calendar?.is_primary ?? false,
    });
  }

  if (
    !hasExistingCalendarSelection &&
    !interviewDataRef.current?.calendar_info?.provider_calendar_id &&
    isDefaultCalendar
  ) {
    updateSharedData('calendar_info', {
      provider_calendar_id: isDefaultCalendar?.provider_calendar_id,
      is_primary: isEdit
        ? (interviewPlan?.scheduled_calendar?.is_primary ?? false)
        : (isDefaultCalendar?.is_primary ?? false),
    });
  }

  const calendarInfo = {
    isConditional: true,
    conditionConfig: { field: 'showDisplayMode', value: 'info' },
    type: 'custom',
    Field: ({ formik }) => {
      const { setFieldValue } = formik || {};
      return (
        <Flex
          w="100%"
          justifyContent="space-between"
          bg="secondary-25"
          p="12px 16px"
          gap="12px"
          borderRadius="6px"
          alignItems="center"
          mb="6px"
          mt="14px"
          h={isLoading && !fetchCalendarNotNeeded ? '44px' : 'auto'}
        >
          {isLoading && !fetchCalendarNotNeeded ? (
            <Box position="relative" left="14px">
              <DotFlashingLoader />
            </Box>
          ) : (
            <Flex gap="6px" w="100%">
              <Flex gap="6px" alignItems="center" w="100%">
                <KText color="secondary-900" size="sm-medium">
                  Calendar:
                </KText>
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  w="100%"
                >
                  <Flex alignItems="center" gap="4px" w="100%">
                    <TruncatedTextWithTooltip
                      text={
                        isEdit
                          ? interviewPlan?.scheduled_calendar?.calendar_name
                          : (() => {
                              // If user has selected a calendar, find its name from options
                              if (
                                interviewPlan?.calendar_info
                                  ?.provider_calendar_id
                              ) {
                                const selectedCalendar =
                                  effectiveCalendarOptions?.calendars?.find(
                                    (cal) =>
                                      cal.provider_calendar_id ===
                                      interviewPlan.calendar_info
                                        .provider_calendar_id
                                  );

                                return (
                                  selectedCalendar?.name ||
                                  isDefaultCalendar?.name
                                );
                              }
                              return isDefaultCalendar?.name;
                            })()
                      }
                      color="secondary-900"
                      maxW={'160px'}
                      toolTipVariant="default"
                    />
                    {isEdit
                      ? interviewPlan?.scheduled_calendar?.is_primary && (
                          <KText color="secondary-900">(You)</KText>
                        )
                      : (() => {
                          // Find the currently displayed calendar (selected or default)
                          const currentCalendarId =
                            interviewPlan?.calendar_info
                              ?.provider_calendar_id ||
                            isDefaultCalendar?.provider_calendar_id;

                          if (currentCalendarId) {
                            const currentCalendar =
                              effectiveCalendarOptions?.calendars?.find(
                                (cal) =>
                                  cal.provider_calendar_id === currentCalendarId
                              );
                            return (
                              currentCalendar?.is_primary && (
                                <KText color="secondary-900">(You)</KText>
                              )
                            );
                          }
                          return null;
                        })()}
                    {connectedCalValue === CALENDAR_PROVIDERS.MICROSOFT && (
                      <Tooltip
                        shouldWrapChildren
                        label={(() => {
                          // In edit mode, use the calendar owner name from API if available
                          if (
                            isEdit &&
                            interviewPlan?.scheduled_calendar?.user_name
                          ) {
                            return `In Outlook, this interview will display ${
                              interviewPlan.scheduled_calendar.user_name ??
                              'User name'
                            } as the organizer as they own this calendar.`;
                          }

                          // Use state variable first (triggers re-render), then fall back
                          // Include scheduled_calendar for edit mode
                          const resolvedCalendarId =
                            currentCalendarId ||
                            interviewDataRef.current?.calendar_info
                              ?.provider_calendar_id ||
                            interviewPlan?.scheduled_calendar
                              ?.provider_calendar_id ||
                            interviewPlan?.calendar_info
                              ?.provider_calendar_id ||
                            isDefaultCalendar?.provider_calendar_id;

                          // Find the calendar owner from the resolved calendar
                          const resolvedCalendar =
                            effectiveCalendarOptions?.calendars?.find(
                              (cal) =>
                                cal.provider_calendar_id === resolvedCalendarId
                            );

                          // If calendar not found, use scheduled_by as fallback
                          const resolvedOwnerUserId =
                            resolvedCalendar?.user_id ||
                            (isEdit ? interviewPlan?.scheduled_by?.id : null);
                          const calendarOwner = users?.find(
                            (user) => user.id === resolvedOwnerUserId
                          );
                          // Use scheduled_by.name as fallback if calendarOwner not found
                          const ownerName =
                            calendarOwner?.name ||
                            (isEdit
                              ? interviewPlan?.scheduled_by?.name
                              : null) ||
                            'the calendar owner';
                          return `In Outlook, this interview will display ${
                            ownerName ?? 'User name'
                          } as the organizer as they own this calendar.`;
                        })()}
                        placement="bottom"
                        hasArrow
                      >
                        <Icon
                          as={InfoCircle}
                          w="16px"
                          h="16px"
                          color="secondary-700"
                          position="relative"
                          bottom={'2px'}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                  <Flex alignItems="flex-end" justifyContent="flex-end">
                    {(isSelfSchedule || !isEdit) &&
                      (isEdit
                        ? interviewPlan?.shared_calendar_enabled
                        : true) && (
                        <Tooltip
                          shouldWrapChildren
                          label={
                            isPrimaryCalendarOnlyAvailable
                              ? "You don't have access to any shared calendars"
                              : ''
                          }
                          placement="left"
                          hasArrow
                        >
                          <Icon
                            as={Edit05}
                            onClick={
                              isPrimaryCalendarOnlyAvailable
                                ? () => {}
                                : () =>
                                    setFieldValue('showDisplayMode', 'calendar')
                            }
                            opacity={isPrimaryCalendarOnlyAvailable ? 0.6 : 1}
                            cursor={
                              isPrimaryCalendarOnlyAvailable
                                ? 'not-allowed'
                                : 'pointer'
                            }
                            position="relative"
                            bottom={
                              isPrimaryCalendarOnlyAvailable ? '2px' : '0px'
                            }
                          />
                        </Tooltip>
                      )}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          )}
        </Flex>
      );
    },
  };

  // Get calendar owner name for the label info text
  const getCalendarLabelInfoText = (calendarId?: string) => {
    if (connectedCalValue !== CALENDAR_PROVIDERS.MICROSOFT) return undefined;

    // In edit mode, use user_name directly from scheduled_calendar if available
    if (isEdit && interviewPlan?.scheduled_calendar?.user_name) {
      return `In Outlook, this interview will display ${
        interviewPlan.scheduled_calendar.user_name ?? 'User name'
      } as the organizer as they own this calendar.`;
    }

    // Get the calendar ID - use state first (triggers re-render), then fall back to ref/props
    // Include scheduled_calendar for edit mode
    const resolvedCalendarId =
      calendarId ||
      currentCalendarId ||
      interviewDataRef.current?.calendar_info?.provider_calendar_id ||
      interviewPlan?.scheduled_calendar?.provider_calendar_id ||
      interviewPlan?.calendar_info?.provider_calendar_id ||
      isDefaultCalendar?.provider_calendar_id;

    // Find the calendar owner from the resolved calendar
    const resolvedCalendar = effectiveCalendarOptions?.calendars?.find(
      (cal) => cal.provider_calendar_id === resolvedCalendarId
    );

    // If calendar not found, use scheduled_by as fallback
    const resolvedOwnerUserId =
      resolvedCalendar?.user_id ||
      (isEdit ? interviewPlan?.scheduled_by?.id : null);
    const calendarOwner = users?.find(
      (user) => user.id === resolvedOwnerUserId
    );
    // Use scheduled_by.name as fallback if calendarOwner not found
    const ownerName =
      calendarOwner?.name ||
      (isEdit ? interviewPlan?.scheduled_by?.name : null) ||
      'the calendar owner';

    return `In Outlook, this interview will display ${
      ownerName ?? 'User name'
    } as the organizer as they own this calendar.`;
  };

  const calendarConfig = {
    isConditional: true,
    conditionConfig: { field: 'showDisplayMode', value: 'calendar' },
    defaultValue: (() => {
      const defaultVal = isEdit
        ? interviewPlan?.scheduled_calendar?.provider_calendar_id
        : interviewPlan?.calendar_info?.provider_calendar_id ||
          isDefaultCalendar?.provider_calendar_id;

      return defaultVal || null;
    })(),
    name: 'calendar_info',
    label: 'Calendar',
    labelInfoText: getCalendarLabelInfoText(),
    type: 'select',
    isLoading: isLoading && !isMaxRetriesExceeded,
    isRequired: false,
    isAppendToBody: true,
    options: effectiveCalendarOptions?.calendars?.map((calendar) => ({
      label: calendar?.is_primary ? (
        `${calendar?.name} (You)`
      ) : calendar?.is_default ? (
        <Flex alignItems="center" gap="6px">
          <KText color="secondary-900" size="sm-medium">
            {calendar?.name}
          </KText>
          <KTag value="Default" color="primary-500" bgColor="secondary-100" />
        </Flex>
      ) : (
        calendar?.name || ''
      ),
      value: calendar?.provider_calendar_id,
      ...calendar,
    })),

    customOnChange: (option) => {
      if (option?.value) {
        // Mark that custom change has occurred
        hasCustomChangeOccurred.current = true;

        const calendarData = {
          provider_calendar_id: option?.value,
          is_primary: option?.is_primary ? option?.is_primary : false,
          // Track that this was a user selection, not auto-assignment
          user_selected: true,
        };

        updateSharedData('calendar_info', calendarData);

        // Update calendarOwnerUserId when calendar changes
        const newCalendarOwnerUserId = getCalendarOwnerUserId(option?.value);
        updateSharedData('calendarOwnerUserId', newCalendarOwnerUserId);
      }
    },
  };

  const fields = [calendarInfo, calendarConfig];

  return { fields };
};
